/**
 * 简笔画绘制原语。
 * 全部线条都带「确定性抖动」：同一 seed 每帧结果一致，
 * 所以线条看起来是手绘的，但不会闪烁。
 */

export const PALETTE = {
  ink: '#3b3733',        // 墨线
  inkSoft: '#6b645c',    // 淡墨
  paper: '#f6efdf',      // 宣纸底
  wood: '#c79a63',       // 木
  woodDark: '#a2743f',
  leaf: '#8fae6b',       // 草叶
  leafDark: '#6f8d4f',
  soil: '#b08356',       // 土
  soilDark: '#8e6540',
  stone: '#cfc9bd',      // 石
  water: '#8fb8c4',      // 水
  cloth: '#d9705e',      // 布/朱砂
  clothBlue: '#6d8fa8',
  gold: '#d9b45b',
  night: '#28304a',
};

const _cache = new Map();
/** 确定性伪随机 [0,1) */
export function rand(seed) {
  let v = _cache.get(seed);
  if (v === undefined) {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
    v = x - Math.floor(x);
    _cache.set(seed, v);
  }
  return v;
}
/** 抖动偏移量，amp 为幅度 */
export function wob(seed, amp = 1) {
  return (rand(seed) - 0.5) * 2 * amp;
}

function strokeStyle(ctx, o) {
  ctx.strokeStyle = o.color || PALETTE.ink;
  ctx.lineWidth = o.width || 1.6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (o.alpha !== undefined) ctx.globalAlpha = o.alpha;
}

/** 手绘直线：中点沿法线轻微偏移，形成微弯 */
export function sline(ctx, x1, y1, x2, y2, o = {}) {
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len < 0.5) return;
  const seed = o.seed ?? (x1 * 7 + y1 * 13 + x2 * 17 + y2 * 19);
  const amp = o.amp ?? 1.2;
  const nx = -(y2 - y1) / len;
  const ny = (x2 - x1) / len;
  const off = wob(seed, Math.min(amp, len * 0.05));
  const mx = (x1 + x2) / 2 + nx * off;
  const my = (y1 + y2) / 2 + ny * off;

  ctx.save();
  strokeStyle(ctx, o);
  ctx.beginPath();
  ctx.moveTo(x1 + wob(seed + 1, amp * 0.4), y1 + wob(seed + 2, amp * 0.4));
  ctx.quadraticCurveTo(mx, my, x2 + wob(seed + 3, amp * 0.4), y2 + wob(seed + 4, amp * 0.4));
  ctx.stroke();
  ctx.restore();
}

/** 手绘圆：分段折线 + 半径抖动 */
export function scircle(ctx, cx, cy, r, o = {}) {
  const seg = o.seg || 12;
  const amp = o.amp ?? (r * 0.06 + 0.4);
  ctx.save();
  strokeStyle(ctx, o);
  ctx.beginPath();
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    const rr = r + wob((o.seed || 0) * 97 + i, amp);
    const x = cx + Math.cos(a) * rr;
    const y = cy + Math.sin(a) * rr * (o.squash || 1);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  if (o.fill) { ctx.fillStyle = o.fill; ctx.fill(); }
  ctx.stroke();
  ctx.restore();
}

export function sellipse(ctx, cx, cy, rx, ry, o = {}) {
  const seg = o.seg || 16;
  const amp = o.amp ?? 0.6;
  ctx.save();
  strokeStyle(ctx, o);
  ctx.beginPath();
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    const k = 1 + wob((o.seed || 0) * 53 + i, 0.04);
    const x = cx + Math.cos(a) * rx * k;
    const y = cy + Math.sin(a) * ry * k + wob((o.seed || 0) * 31 + i, amp);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
  if (o.fill) { ctx.fillStyle = o.fill; ctx.fill(); }
  ctx.stroke();
  ctx.restore();
}

/** 手绘矩形（四条独立抖线，角落不闭合，更像手绘） */
export function srect(ctx, x, y, w, h, o = {}) {
  const s = o.seed || 0;
  sline(ctx, x, y, x + w, y, { ...o, seed: s + 1 });
  sline(ctx, x + w, y, x + w, y + h, { ...o, seed: s + 2 });
  sline(ctx, x + w, y + h, x, y + h, { ...o, seed: s + 3 });
  sline(ctx, x, y + h, x, y, { ...o, seed: s + 4 });
}

/** 手绘多边形（自动闭合，用于家具、屋顶等） */
export function spoly(ctx, pts, o = {}) {
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    sline(ctx, a[0], a[1], b[0], b[1], { ...o, seed: (o.seed || 0) + i });
  }
}

/** 淡色填充（不描边），用于先铺色再画线 */
export function fillPoly(ctx, pts, color, alpha = 1) {
  if (pts.length < 3) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/** 圆弧（指定起止角，用于笑脸、盆、井口） */
export function sarc(ctx, cx, cy, r, a0, a1, o = {}) {
  ctx.save();
  strokeStyle(ctx, o);
  ctx.beginPath();
  ctx.arc(cx, cy, r, a0, a1);
  ctx.stroke();
  ctx.restore();
}

/** 排线阴影：在矩形区域内画斜线，简笔画常用手法 */
export function hatch(ctx, x, y, w, h, o = {}) {
  const gap = o.gap || 5;
  const seed = o.seed || 0;
  ctx.save();
  strokeStyle(ctx, { color: o.color || PALETTE.inkSoft, width: o.width || 1, alpha: o.alpha ?? 0.5 });
  for (let i = -h; i < w; i += gap) {
    const j = wob(seed + i, 0.8);
    ctx.beginPath();
    ctx.moveTo(x + i + j, y + h);
    ctx.lineTo(x + i + h + j, y);
    ctx.stroke();
  }
  ctx.restore();
}

/** 手绘感文字（轻微旋转 + 墨色） */
export function stext(ctx, text, x, y, o = {}) {
  ctx.save();
  ctx.fillStyle = o.color || PALETTE.ink;
  ctx.font = o.font || '13px "LXGW WenKai Lite", "Kaiti SC", "STKaiti", "KaiTi", "Songti SC", serif';
  ctx.textAlign = o.align || 'center';
  ctx.textBaseline = o.baseline || 'middle';
  ctx.globalAlpha = o.alpha ?? 1;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/** 脚下的椭圆阴影 */
export function shadow(ctx, x, y, rx, ry = rx * 0.35, alpha = 0.18) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
